import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { getAdminStats, getExhibitorEnquiries, getContactMessages } from '../../api/client';
import { Users, MessageSquare, Mail, TrendingUp, Bell, RefreshCw, Clock, Building2, Globe, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalEnquiries: 0,
    newEnquiries: 0,
    totalMessages: 0,
    newMessages: 0
  });
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [prevNewCount, setPrevNewCount] = useState(0);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      const [statsData, enquiriesData, messagesData] = await Promise.all([
        getAdminStats(),
        getExhibitorEnquiries({ limit: 5 }),
        getContactMessages({ limit: 5 })
      ]);
      
      // Check for new enquiries
      const totalNew = statsData.newEnquiries + statsData.newMessages;
      if (prevNewCount > 0 && totalNew > prevNewCount) {
        toast.success(`🔔 New enquiry received!`, {
          description: 'Check your dashboard for details'
        });
        // Try browser notification
        if (Notification.permission === 'granted') {
          new Notification('ASEAN Business Forum - New Enquiry', {
            body: 'You have received a new enquiry!',
            icon: '/favicon.ico'
          });
        }
      }
      setPrevNewCount(totalNew);
      
      setStats(statsData);
      setRecentEnquiries(enquiriesData.enquiries || []);
      setRecentMessages(messagesData.messages || []);
      setLastUpdate(new Date());
      
      if (showToast) {
        toast.success('Dashboard refreshed');
      }
    } catch {
      // Dashboard will show empty state
    } finally {
      setLoading(false);
    }
  }, [prevNewCount]);

  useEffect(() => {
    fetchData();
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const statCards = [
    {
      title: 'Exhibitor Enquiries',
      value: stats.totalEnquiries,
      badge: stats.newEnquiries,
      description: stats.newEnquiries > 0 ? `${stats.newEnquiries} new - needs attention!` : 'All reviewed',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/admin/enquiries'
    },
    {
      title: 'Contact Messages',
      value: stats.totalMessages,
      badge: stats.newMessages,
      description: stats.newMessages > 0 ? `${stats.newMessages} new - needs attention!` : 'All reviewed',
      icon: MessageSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      link: '/admin/messages'
    },
    {
      title: 'Pending Review',
      value: stats.newEnquiries + stats.newMessages,
      description: 'Total items needing action',
      icon: Bell,
      color: stats.newEnquiries + stats.newMessages > 0 ? 'text-red-600' : 'text-gray-600',
      bgColor: stats.newEnquiries + stats.newMessages > 0 ? 'bg-red-50' : 'bg-gray-50',
      highlight: stats.newEnquiries + stats.newMessages > 0
    },
    {
      title: 'Response Rate',
      value: stats.totalEnquiries > 0 
        ? `${Math.round(((stats.totalEnquiries - stats.newEnquiries) / stats.totalEnquiries) * 100)}%`
        : '0%',
      description: 'Enquiries reviewed',
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
            <p className="text-gray-600 mt-1">
              <Clock size={14} className="inline mr-1" />
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          </div>
          <Button onClick={() => fetchData(true)} variant="outline" size="sm">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* Alert Banner for New Items */}
        {(stats.newEnquiries > 0 || stats.newMessages > 0) && (
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <Bell size={24} className="animate-pulse" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">You have new notifications!</h3>
                  <p className="text-white/90">
                    {stats.newEnquiries > 0 && `${stats.newEnquiries} new enquir${stats.newEnquiries > 1 ? 'ies' : 'y'}`}
                    {stats.newEnquiries > 0 && stats.newMessages > 0 && ' • '}
                    {stats.newMessages > 0 && `${stats.newMessages} new message${stats.newMessages > 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              <a href="/admin/enquiries" className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold hover:bg-orange-50 transition-colors">
                View Now
              </a>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={stat.title} 
                className={`hover:shadow-lg transition-all cursor-pointer ${stat.highlight ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}
                onClick={() => stat.link && (window.location.href = stat.link)}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <div className="relative">
                    <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                    {stat.badge > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {stat.badge}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <p className={`text-xs mt-1 ${stat.badge > 0 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Enquiries */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Enquiries</CardTitle>
                <CardDescription>Latest exhibitor requests</CardDescription>
              </div>
              <a href="/admin/enquiries" className="text-sm text-blue-600 hover:underline flex items-center">
                View all <ChevronRight size={16} />
              </a>
            </CardHeader>
            <CardContent>
              {recentEnquiries.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No enquiries yet</p>
              ) : (
                <div className="space-y-3">
                  {recentEnquiries.map((enquiry) => (
                    <div 
                      key={enquiry._id || enquiry.email} 
                      className={`p-3 rounded-lg border ${enquiry.status === 'new' ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{enquiry.name}</span>
                            {enquiry.status === 'new' && (
                              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">NEW</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Building2 size={12} /> {enquiry.company}
                            </span>
                            <span className="flex items-center gap-1">
                              <Globe size={12} /> {enquiry.country}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">{enquiry.category}</div>
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(enquiry.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Messages */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Messages</CardTitle>
                <CardDescription>Latest contact form submissions</CardDescription>
              </div>
              <a href="/admin/messages" className="text-sm text-blue-600 hover:underline flex items-center">
                View all <ChevronRight size={16} />
              </a>
            </CardHeader>
            <CardContent>
              {recentMessages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No messages yet</p>
              ) : (
                <div className="space-y-3">
                  {recentMessages.map((msg) => (
                    <div 
                      key={msg._id || msg.email} 
                      className={`p-3 rounded-lg border ${msg.status === 'new' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{msg.name}</span>
                            {msg.status === 'new' && (
                              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">NEW</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{msg.message}</p>
                          <div className="text-xs text-gray-400 mt-1">{msg.email}</div>
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a
                href="/admin/enquiries"
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all"
              >
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Users className="text-blue-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Manage Enquiries</h3>
                  <p className="text-sm text-gray-600">Review & respond</p>
                </div>
              </a>
              <a
                href="/admin/messages"
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-all"
              >
                <div className="bg-green-100 p-2 rounded-lg">
                  <MessageSquare className="text-green-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">View Messages</h3>
                  <p className="text-sm text-gray-600">Contact submissions</p>
                </div>
              </a>
              <a
                href="/admin/editor"
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-all"
              >
                <div className="bg-purple-100 p-2 rounded-lg">
                  <TrendingUp className="text-purple-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Visual Editor</h3>
                  <p className="text-sm text-gray-600">Edit site content</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
